/**
 * Internal customer support form (`/support`).
 *
 * GitHub issue #544: "P0: Slack workspace invites failing for customers —
 * domain restriction". The `herohire` Slack workspace only allows
 * `@herohire.ai`/`@iii.partners` email addresses to accept the shared
 * invite link — a customer with their own email domain (e.g.
 * jane@acme-corp.com) is rejected by Slack ("This workspace is only open
 * to people with certain email addresses") and never joins. Per Robert
 * Syfert (standup 2026-06-03): "We need to get rid of Slack" — most old
 * customers never got an invite in the first place, and only a handful of
 * people use it.
 *
 * This screen is Option B from that ticket: every customer, regardless of
 * email domain or whether they ever received a (now-dead) Slack invite,
 * reaches support the same way — a simple in-app form that posts to
 * `POST /api/v1/support` (`apps/workers/src/api/support.ts`) and creates a
 * durable `support_tickets` row. There is no `invite_sent_at` check or
 * Slack-link fallback anywhere in this flow: it is intentionally the same
 * single path for every customer (see the ticket's [edge] case — a
 * customer with `customers.invite_sent_at IS NULL` must land here too,
 * not on a dead Slack link).
 */

import { useState } from 'react';
import { useSubmitSupportTicket } from '@/lib/api/apiClient';

export const Support: React.FC = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const { mutate, isPending, isSuccess, data, reset } = useSubmitSupportTicket();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(
      { subject, body },
      {
        onSuccess: () => {
          setSubject('');
          setBody('');
        },
      }
    );
  };

  if (isSuccess && data) {
    return (
      <section className="bg-white rounded-lg shadow p-6" data-testid="support-ticket-success">
        <h2 className="text-2xl font-bold mb-4">Thanks — we got your message</h2>
        <p className="text-gray-700 mb-4">
          Your support request has been submitted (ticket status: {data.status}). We&apos;ll get
          back to you by email.
        </p>
        <button
          type="button"
          onClick={() => reset()}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
        >
          Submit another request
        </button>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-2">Contact support</h2>
      <p className="text-gray-600 mb-6">
        Send us a message and we&apos;ll get back to you as soon as we can.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="support-subject" className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            id="support-subject"
            name="subject"
            type="text"
            required
            maxLength={255}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label htmlFor="support-body" className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            id="support-body"
            name="body"
            required
            maxLength={10000}
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <button
          type="submit"
          disabled={isPending || !subject.trim() || !body.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
        >
          {isPending ? 'Sending...' : 'Send message'}
        </button>
      </form>
    </section>
  );
};

export default Support;
